// src/features/locker-controllers/components/LockerControllerEdit.tsx - VERSIÓN CORREGIDA
import React, { useState } from 'react';
import {
  Edit,
  SimpleForm,
  TextInput,
  NumberInput,
  required,
  useRecordContext,
  LoadingIndicator,
  useNotify,
  useRedirect,
  useTranslate,
  usePermissions,
  ReferenceField,
  TextField,
} from 'react-admin';
import { 
  Box,
  Alert,
  Typography,
  Grid,
} from '@mui/material';

// COMPONENTES DEL SISTEMA
import { MyToolbar } from '../../../components/forms/MyToolbar';
import { MyFormContainer } from '../../../components/forms/MyFormContainer';
import { 
  CenteredFormLayout,
  MyFormSection, 
} from '../../../components/forms/MyFormLayout';
import BaseDeleteDialog from '../../../components/common/BaseDeleteDialog';

// Hooks y tipos
import { useLockerControllerPermissions } from '../hooks/useLockerControllerPermissions';
import { useLockerControllerValidation } from '../hooks/useLockerControllerValidation';
import { LockerControllerDetailResponse } from '../types';

const LockerControllerEditTitle: React.FC = () => {
  const record = useRecordContext<LockerControllerDetailResponse>();
  const translate = useTranslate();
  
  if (!record) return <LoadingIndicator />;
  
  return (
    <span>
      {translate('resources.locker-controllers.titles.edit', 'Editar Controlador')}: {record.name}
    </span>
  );
};

const LockerControllerEdit: React.FC = (props) => {
  const { permissions } = usePermissions();
  const translate = useTranslate();
  const notify = useNotify();
  const redirect = useRedirect();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  

  const role = permissions?.role || localStorage.getItem('role');
  if (!role || !['SuperAdmin', 'GroupAdmin', 'HotelAdmin'].includes(role)) {
    return (
      <MyFormContainer maxWidth="md">
        <Alert severity="error">
          {translate('ra.page.forbidden', 'No tienes permisos para acceder a esta página')}
        </Alert>
      </MyFormContainer>
    );
  }

 const transform = (data: any) => {

  const record = data;
  return {
    name: data.name?.trim(),
    ipAddress: data.ipAddress?.trim(),
    tcpPort: typeof data.tcpPort === 'string' ? parseInt(data.tcpPort, 10) : data.tcpPort,
    zoneId: record.zone?.id, 
  };
};

  const handleSuccess = (data: any) => {
    notify('resources.locker-controllers.messages.update_success', { 
      type: 'success',
      messageArgs: { name: data?.name }
    });
    redirect('list', 'locker-controllers');
  };

  const handleError = (error: any) => {
    console.error('Mutation error:', error);
    notify(
      error?.body?.message || 
      error?.message ||
      'resources.locker-controllers.messages.update_error', 
      { type: 'error' }
    );
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  return (
    <Edit 
      {...props} 
      title={<LockerControllerEditTitle />}
      transform={transform}
      mutationOptions={{
        onSuccess: handleSuccess,
        onError: handleError
      }}
      redirect={false} 
      mutationMode='pessimistic'
    >
      <LockerControllerEditForm onDelete={handleDeleteClick} />
      
      <LockerControllerDeleteDialog 
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      />
    </Edit>
  );
};

const LockerControllerEditForm: React.FC<{ onDelete?: () => void }> = ({ onDelete }) => {
  const record = useRecordContext<LockerControllerDetailResponse>();
  const translate = useTranslate();
  const redirect = useRedirect();
  
  const {
    validateName,
    validateIpAddress,
    validateTcpPort,
  } = useLockerControllerValidation();
  
  const { 
    canEditLockerControllers, 
    canDeleteLockerControllers 
  } = useLockerControllerPermissions(record);

  if (!record) return <LoadingIndicator />;
  
  if (!canEditLockerControllers) {
    return (
      <Alert severity="error">
        {translate('resources.locker-controllers.messages.no_permission_edit')}
      </Alert>
    );
  }

  const handleCancel = () => redirect('list', 'locker-controllers');

  return (
    <MyFormContainer>
      <SimpleForm 
        toolbar={
          <MyToolbar 
            onCancel={handleCancel}
            resource="locker-controllers"
            showDelete={canDeleteLockerControllers}
            onDelete={onDelete}
          />
        }
      >
       <CenteredFormLayout>
       
          <MyFormSection variant='outlined'
          title={translate('resources.locker-controllers.ui.edit_title', 'Modificar Controlador de Taquilla')}>
        
              {/* Nombre del controlador */}
              <TextInput
                source="name"
                label={translate('resources.locker-controllers.fields.name')}
                validate={[required(), validateName]}
                fullWidth
                size="small"
              />
              
              {/* Hotel - Solo lectura */}
              <Box>
                <Typography variant="caption" color="textSecondary" gutterBottom>
                  {translate('resources.locker-controllers.fields.hotel')}
                </Typography>
                <ReferenceField
                  source="hotelId"
                  reference="hotels"
                  link={false}
                >
                  <TextField source="name" />
                </ReferenceField>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} md={8}>
                  <TextInput
                    source="ipAddress"
                    label={translate('resources.locker-controllers.fields.ipAddress')}
                    validate={[required(), validateIpAddress]}
                    fullWidth
                    size="small"
                    placeholder="192.168.1.100"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <NumberInput
                    source="tcpPort"
                    label={translate('resources.locker-controllers.fields.tcpPort')}
                    validate={[required(), validateTcpPort]}
                    fullWidth
                    min={1}
                    max={65535}
                    size="small"
                  />
                </Grid>
              </Grid>
              
           </MyFormSection>
        </CenteredFormLayout>

      
      </SimpleForm>
    </MyFormContainer>
  );
};


const LockerControllerDeleteDialog: React.FC<{
  open: boolean;
  onClose: () => void;
}> = ({ open, onClose }) => {
  const record = useRecordContext<LockerControllerDetailResponse>();
  const translate = useTranslate();
  
  if (!record) return null;
  
  return (
    <BaseDeleteDialog
      open={open}
      onClose={onClose}
      recordId={record.id}
      resource="locker-controllers"
      warningMessage={
        record.lockersCount && record.lockersCount > 0
          ? translate('resources.locker-controllers.messages.has_associated_lockers', {
              count: record.lockersCount
            })
          : undefined
      }
    />
  );
};

export default LockerControllerEdit;